import getEnvValue from '@/commons/getEnvValue';
import isChunkLoadError, { isChunkLoadMessage } from '@/utils/isChunkLoadError';
import { version } from './version';

const SENTRY_ENVIRONMENT_LOCAL = 'local';
const SENTRY_ENVIRONMENT_UNKNOWN = 'unknown';

const sentryEnvironment =
  getEnvValue('NEXT_PUBLIC_ENV_NAME') ?? SENTRY_ENVIRONMENT_UNKNOWN;

const isProduction =
  sentryEnvironment?.includes('prod') || sentryEnvironment?.includes('stg');
const isLocal = sentryEnvironment === SENTRY_ENVIRONMENT_LOCAL;

const isLocalSentryEnabled =
  getEnvValue('NEXT_PUBLIC_SENTRY_LOCAL_ENABLED') === 'true';

const isSentryDebuggingEnabled =
  getEnvValue('NEXT_PUBLIC_SENTRY_DEBUGGING_ENABLED') === 'true';

const sentryDsn = getEnvValue('NEXT_PUBLIC_SENTRY_DSN');

// True if any of the last N breadcrumbs is a failed fetch/xhr to `hostMatch`.
// Used to corroborate that an exception came from a specific vendor SDK.
const hasRecentFailedNetworkCall = (event, hostMatch, lookback = 5) => {
  const values = event?.breadcrumbs?.values;
  const breadcrumbs = Array.isArray(values)
    ? values
    : Array.isArray(event?.breadcrumbs)
    ? event.breadcrumbs
    : [];
  return breadcrumbs.slice(-lookback).some(bc => {
    if (bc?.category !== 'fetch' && bc?.category !== 'xhr') return false;
    if (!(bc?.data?.url || '').includes(hostMatch)) return false;
    const status = bc?.data?.status_code;
    return status === 0 || status === undefined || status >= 400;
  });
};

// True if any exception frame's filename contains `pattern`. Used to scope a
// filter to a specific vendor SDK so future first-party throws aren't silenced.
const hasFrameMatching = (event, pattern) =>
  (event?.exception?.values || []).some(exc =>
    (exc?.stacktrace?.frames || []).some(f =>
      (f?.filename || '').includes(pattern)
    )
  );

// When a promise rejects with a non-Error value (commonly bare `reject()` with no
// argument), Sentry wraps it as "Non-Error promise rejection captured with value:
// …". hint.originalException is then the raw rejection value (often `undefined`),
// so checking originalException.message alone misses these events.
const getExceptionMessage = (event, hint) => {
  const originalException = hint?.originalException;
  if (typeof originalException === 'string') {
    return originalException;
  }
  if (originalException?.message) {
    return originalException.message;
  }

  return (event?.exception?.values || [])
    .map(exc => exc?.value)
    .filter(Boolean)
    .join(' ');
};

export const isNonErrorPromiseRejection = (event, hint) =>
  getExceptionMessage(event, hint).includes('Non-Error promise rejection');

// CON-8174 — Cylindo 3D-viewer SDK fails its config XHR (network drop / blocker).
// Corroborated by a failed content.cylindo.com call in recent breadcrumbs.
const CYLINDO_HOST = 'content.cylindo.com';
const isCylindoAxiosError = (error, event) => {
  if (
    error?.name === 'AxiosError' &&
    error?.message === 'Network Error' &&
    hasRecentFailedNetworkCall(event, CYLINDO_HOST)
  ) {
    return true;
  }
  if (error?.name !== 'TypeError' || typeof error?.message !== 'string') {
    return false;
  }
  const msg = error.message;
  return (
    msg.includes(CYLINDO_HOST) &&
    (msg.includes('Failed to fetch') ||
      msg.includes('Load failed') ||
      msg.includes('NetworkError'))
  );
};

// CON-8175 — Pinterest conversion tracker blocked by ad-blockers. Match the
// three browser phrasings, scoped to the Pinterest host.
const PINTEREST_HOST = 'ct.pinterest.com';
const isPinterestFetchFailure = error => {
  if (error?.name !== 'TypeError' || typeof error?.message !== 'string') {
    return false;
  }
  const msg = error.message;
  return (
    msg.includes(PINTEREST_HOST) &&
    (msg.includes('Failed to fetch') ||
      msg.includes('Load failed') ||
      msg.includes('NetworkError'))
  );
};

// CON-8176 — OneTrust otSDKStub.js null-derefs `.Domain` after its geolocation
// XHR fails. Frame check scopes this to OneTrust so first-party `.Domain`
// access throwing the same message in the future would still reach Sentry.
const isOneTrustDomainError = (error, event) =>
  error?.name === 'TypeError' &&
  typeof error?.message === 'string' &&
  error.message.includes("reading 'Domain'") &&
  hasFrameMatching(event, 'otSDKStub.js');

// CON-8365 - BazaarVoice's reviews widget (swat_reviews bundle, loaded by bv.js)
// calls IntersectionObserver.observe() on a non-Element target from inside its own
// post-render effect. This surfaces as an unhandled promise rejection ~80k times a
// month, almost entirely in in-app webviews (Facebook/Instagram/Snapchat/WeChat/
// etc.) where the widget element is detached/hidden when the observer fires. Review
// content still loads (the data fetch returns 200 before this throws), so there is
// no customer-facing impact and no first-party DOM element involved - the throw is
// internal to BazaarVoice (vendor ticket raised separately). The frame check scopes
// this to the swat_reviews bundle so a future first-party IntersectionObserver
// misuse throwing the same message would still reach Sentry. Three browser phrasings
// observed in prod, all the same root cause: Chromium/WebKit "...parameter 1 is not of
// type 'Element'.", Firefox "Argument 1 ('target') to IntersectionObserver.observe must
// be an instance of Element", and older Firefox "IntersectionObserver.observe: Argument 1
// is not an object.".
const isBazaarVoiceObserverError = (error, event) => {
  if (error?.name !== 'TypeError' || typeof error?.message !== 'string') {
    return false;
  }
  const msg = error.message;
  return (
    msg.includes('IntersectionObserver') &&
    (msg.includes("not of type 'Element'") ||
      msg.includes('must be an instance of Element') ||
      msg.includes('Argument 1 is not an object')) &&
    hasFrameMatching(event, 'swat_reviews')
  );
};

// Facebook iOS in-app browser - Meta-injected LCP bridge calls
// window.webkit.messageHandlers without a registered handler. Not Joybird code;
// confirmed iOS + Facebook only in Sentry. Harmless telemetry noise.
const isFacebookIosWebkitBridgeError = (error, event) => {
  if (error?.name !== 'TypeError' || typeof error?.message !== 'string') {
    return false;
  }
  if (!error.message.includes('window.webkit.messageHandlers')) {
    return false;
  }

  const browserName = event?.contexts?.browser?.name || '';
  const osName = event?.contexts?.os?.name || '';
  if (/facebook/i.test(browserName) && /iOS|iPadOS/i.test(osName)) {
    return true;
  }

  const headers = event?.request?.headers;
  const ua = headers?.['User-Agent'] || headers?.['user-agent'] || '';
  return (
    /FBAN|FBAV|FB_IAB|FBIOS|FBSS/i.test(ua) && /iPhone|iPad|iPod/i.test(ua)
  );
};

// JOYBIRD-40-5QA6 — unrelated call sites share one runtime message, so Sentry
// groups them. Fingerprint by boundary so each bucket is countable on its own.
const isChunkLoadFailure = (event, hint) =>
  isChunkLoadError(hint?.originalException) ||
  isChunkLoadMessage(getExceptionMessage(event, hint));

// `component` is a fallback for capture sites not yet on the errorBoundary tag.
const getChunkLoadOrigin = event =>
  event?.tags?.errorBoundary || event?.tags?.component || 'unknown';

const sharedSentryConfig = {
  dsn: sentryDsn,
  // By default Sentry is only enabled in non-local environments, but to enable it locally for debugging
  // we can set an environment variable in .env.local: NEXT_PUBLIC_SENTRY_LOCAL_ENABLED=true
  enabled: !!sentryDsn && (!isLocal || isLocalSentryEnabled),
  environment: sentryEnvironment,
  release: version,

  // Performance monitoring - configurable via environment variable.
  // Shared control across Sentry, server-side OTel, and the Dash0 Web SDK gate
  // in instrumentation-client.js (see .github/instructions/tracing.instructions.md).
  tracesSampleRate: parseFloat(
    getEnvValue('NEXT_PUBLIC_TRACING_SAMPLE_RATE') || '0.001'
  ),

  // Enhanced error tracking with filtering
  ignoreErrors: [
    /^(?:UnhandledRejection:\s*)?Non-Error promise rejection captured/,
  ],
  beforeSend(event, hint) {
    if (isProduction && event.exception) {
      const error = hint.originalException;
      if (isNonErrorPromiseRejection(event, hint)) {
        return null;
      }
      if (
        isCylindoAxiosError(error, event) ||
        isPinterestFetchFailure(error) ||
        isOneTrustDomainError(error, event) ||
        isBazaarVoiceObserverError(error, event) ||
        isFacebookIosWebkitBridgeError(error, event)
      ) {
        return null;
      }
    }
    if (event.exception && isChunkLoadFailure(event, hint)) {
      const chunkLoadOrigin = getChunkLoadOrigin(event);
      event.tags = { ...event.tags, chunkLoadOrigin };
      event.fingerprint = ['chunk-load-error', chunkLoadOrigin];
    }
    return event;
  },

  // Debugging - only when explicitly enabled
  debug: isSentryDebuggingEnabled,

  // Attach request headers and IP for users, for better debugging
  sendDefaultPii: true,

  maxBreadcrumbs: 30,
  attachStacktrace: true,
};

export default sharedSentryConfig;
